import { useCallback, useEffect, type MouseEvent } from 'react';
import { flushSync } from 'react-dom';
import { usePrefersReducedMotion } from '../../../shared/hooks/use-reduced-motion';
import { THEME_LABEL } from '../constants/theme.constant';
import type { ThemeMode } from '../models/theme.model';
import { useThemeStore } from '../stores/theme.store';

export interface ThemeToggleState {
  readonly mode: ThemeMode;
  readonly label: string;
  readonly toggle: (event: MouseEvent<HTMLButtonElement>) => void;
}

function applyMode(mode: ThemeMode): void {
  document.documentElement.classList.toggle('dark', mode === 'dark');
}

export function useApplyTheme(): void {
  const mode = useThemeStore((s) => s.mode);
  useEffect(() => applyMode(mode), [mode]);
}

export function useThemeToggle(): ThemeToggleState {
  const mode = useThemeStore((s) => s.mode);
  const setMode = useThemeStore((s) => s.setMode);
  const reduced = usePrefersReducedMotion();

  const toggle = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      const next: ThemeMode = mode === 'dark' ? 'light' : 'dark';
      const commit = (): void => {
        flushSync(() => setMode(next));
        applyMode(next);
      };
      if (reduced || typeof document.startViewTransition !== 'function') {
        commit();
        return;
      }
      const rect = event.currentTarget.getBoundingClientRect();
      const root = document.documentElement.style;
      root.setProperty('--theme-x', `${rect.left + rect.width / 2}px`);
      root.setProperty('--theme-y', `${rect.top + rect.height / 2}px`);
      document.startViewTransition(commit);
    },
    [mode, reduced, setMode],
  );

  return { mode, label: THEME_LABEL[mode], toggle };
}
